import { CircularProgress, makeStyles } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import _pick from 'lodash/pick';
import schedulingWizardApi, {
  SchedulingWizardId,
} from '../../api/schedulingWizard';
import { TenantId } from '../../api/tenants/Tenant';
import { VentureId } from '../../api/ventures/types/Venture';
import { Button, SnackMessage, Text } from '../../components/common';
import { COLORS } from '../../theme/variables';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    padding: 0,
    height: '100%',

    [theme.breakpoints.up('xs')]: {
      width: 'calc(100% - 60px)',
    },

    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  block: {
    width: '700px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: 0,
    boxSizing: 'border-box',

    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
    },
  },
  loadingContainer: {
    height: '100vh',
    padding: 0,
    alignItems: 'center',

    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    borderRadius: '16px',
  },
  successIcon: {
    fontSize: 30,
    color: COLORS.COLOR_GREEN_BASE,
    marginBottom: 15,
  },
  errorIcon: {
    fontSize: 30,
    color: COLORS.COLOR_RED_BASE,
    marginBottom: 15,
  },
  successText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
    textAlign: 'center',
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
}));

function parseFilterFromURL(search: string): any {
  return _pick(parseQuery(search), ['key']);
}

function getTenantAndVentureIdFromKey(search: string): {
  tenantId: TenantId | null;
  wizardId: SchedulingWizardId | null;
  ventureId: VentureId | null;
} {
  try {
    const { key } = parseFilterFromURL(search);
    const decodedKey = atob(key);
    const [tenantId, wizardId, ventureId] = decodedKey.split('|');

    return {
      tenantId: tenantId as TenantId,
      wizardId: wizardId as SchedulingWizardId,
      ventureId: ventureId as VentureId,
    };
  } catch (error) {
    return { tenantId: null, wizardId: null, ventureId: null };
  }
}

function StartWizardPage() {
  const classes = useStyles();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [isSuccess, setIsSuccess] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const { tenantId, ventureId } = useMemo(
    () => getTenantAndVentureIdFromKey(location.search),
    [location.search],
  );

  const handleRestart = async () => {
    try {
      setIsStarting(true);

      if (!ventureId || !tenantId) {
        const error: any = new Error('Invalid or missing key');
        error.response = {
          data: {
            message: 'Invalid or missing key',
          },
        };
        throw error;
      }

      await schedulingWizardApi.createPublic(
        tenantId as TenantId,
        ventureId as VentureId,
      );

      setIsSuccess(true);
    } catch (e: any) {
      const messageError = e.response?.data?.message || 'Internal server error';
      enqueueSnackbar('An error occurred while starting the wizard', {
        variant: 'error',
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setIsStarting(false);
    }
  };

  useEffect(() => {
    document.body.style.minWidth = 'auto';
  }, []);

  if (isSuccess) {
    return (
      <div
        data-testid='report-page-restarted'
        className={cn(classes.container, classes.loadingContainer)}>
        <div className={cn(classes.block, classes.successBlock)}>
          <CheckCircleIcon className={classes.successIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              Thank you!
              <br />
              New Scheduling Wizard was started. You will receive voting links
              in your email shortly.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid='report-page-error'
      className={cn(classes.container, classes.loadingContainer)}>
      <div className={cn(classes.block, classes.successBlock)}>
        <CancelIcon className={classes.errorIcon} />
        <div className={classes.successText}>
          <Text variant='normal'>
            There was a problem with the wizard you requested, you may be using
            an outdated link for an old wizard.
            <br />
            Please use the button below to start a new wizard.
          </Text>
          <Button
            variant='contained'
            color='primary'
            onClick={handleRestart}
            disabled={isStarting}>
            {isStarting ? (
              <CircularProgress size={20} color='primary' />
            ) : (
              'Start Wizard'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default StartWizardPage;
